import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/veteran-facing-services-tools/veteran-facing-services-tools/packages/documentation/src/layouts/SidebarLayout.jsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "writing-an-end-to-end-test"
    }}>{`Writing an end-to-end test`}</h1>
    <p>{`Front end engineers use end-to-end (e2e) tests in `}<inlineCode parentName="p">{`vets-website`}</inlineCode>{` to validate multipage applications with client-side routing. They are primarily used to assert that:`}</p>
    <ul>
      <li parentName="ul">{`client applications render their inputs`}</li>
      <li parentName="ul">{`client-side navigation occurs when the required fields are populated`}</li>
    </ul>
    <h2 {...{
      "id": "end-to-end-testing-overview"
    }}>{`End-to-end testing overview`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`vets-website`}</inlineCode>{` uses `}<a parentName="li" {...{
          "href": "https://www.cypress.io/"
        }}>{`Cypress`}</a>{` to write end-to-end tests. See `}<a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/va.gov-team/blob/master/platform/testing/end-to-end/cypress-best-practices-on-vsp.md"
        }}>{`Cypress Best Practices on VSP`}</a>{` and `}<a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/va.gov-team/blob/master/platform/testing/end-to-end/vsp-cypress-resources.md"
        }}>{`Cypress Resources Reference Guide`}</a>{` for detailed use cases and documented helpers/mocks.`}
        <ul parentName="li">
          <li parentName="ul">{`Some older end-to-end tests were written in `}<a parentName="li" {...{
              "href": "https://nightwatchjs.org"
            }}>{`Nightwatch`}</a>{` prior to Cypress. All new tests should be written using Cypress moving forward and Nightwatch tests are in the process of being deprecated/migrated to Cypress.`}</li>
          <li parentName="ul">{`Refer to the `}<a parentName="li" {...{
              "href": "https://github.com/department-of-veterans-affairs/va.gov-team/blob/master/platform/testing/end-to-end/cypress-migration-guide.md"
            }}>{`Cypress migration guide`}</a>{` to convert old tests or write new tests.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`End-to-end tests are `}<strong parentName="li">{`collocated in application folder`}</strong>{` with the application they test`}</li>
      <li parentName="ul">{`Cypress tests can be run using the command `}<inlineCode parentName="li">{`yarn cy:run`}</inlineCode>{` (after `}<inlineCode parentName="li">{`yarn watch`}</inlineCode>{` to `}<inlineCode parentName="li">{`yarn build`}</inlineCode>{`).`}</li>
    </ul>
    <h2 {...{
      "id": "end-to-end-tests-conventions"
    }}>{`End-to-end tests conventions`}</h2>
    <ul>
      <li parentName="ul">{`Use a comment to indicate what page is being tested`}</li>
      <li parentName="ul">{`Disable scrolling`}</li>
      <li parentName="ul">{`Assert navigation is successful`}</li>
      <li parentName="ul">{`Use functions from the helper file to perform all actions on the page`}</li>
    </ul>
    <p><em parentName="p">{`These are recommendations, not requirements, except where labeled as 'REQUIRED'`}</em></p>
    <ul>
      <li parentName="ul">{`Separate navigation from field input`}
        <ul parentName="li">
          <li parentName="ul">{`Use a `}<strong parentName="li">{`main test file`}</strong>{` for navigation, assertions, and calls helpers`}</li>
          <li parentName="ul">{`Use a `}<strong parentName="li">{`helper file`}</strong>{` for filling out forms`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Create separate, numbered `}<strong parentName="li">{`main test files`}</strong>{` to organize tests by their focus:`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`00-all-fields.cypress.spec.js`}</strong>{` - required and optional fields`}</li>
          <li parentName="ul"><strong parentName="li">{`01-required.cypress.spec.js`}</strong>{` - only required fields`}</li>
          <li parentName="ul"><strong parentName="li">{`02-accessibility.cypress.spec.js`}</strong>{` - validates accessibility`}</li>
          <li parentName="ul"><strong parentName="li">{`03-auth.cypress.spec.js`}</strong>{` - validates authentication`}</li>
          <li parentName="ul"><strong parentName="li">{`04-cross-cutting-feature.cypress.spec.js`}</strong>{` - validates one feature used across several pages (e.g. save in progress)`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Group tests by pages and use a comment to indicate what page is being tested`}</li>
      <li parentName="ul">{`Mock all api responses before starting the test. See `}<a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/va.gov-team/tree/master/platform/testing/end-to-end#mocks"
        }}>{`Mock API responses`}</a></li>
      <li parentName="ul">{`Use `}<inlineCode parentName="li">{`waitForElementVisible`}</inlineCode>{` before interacting with any element on the page`}</li>
      <li parentName="ul">{`Use `}<inlineCode parentName="li">{`Timeouts`}</inlineCode>{` constants for setting timeouts (`}<inlineCode parentName="li">{`platform/testing/e2e/timeouts.js`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`Use helpers for filling data and performing actions on the page`}</li>
      <li parentName="ul">{`(`}<em parentName="li">{`REQUIRED`}</em>{`) Perform `}<inlineCode parentName="li">{`axeCheck`}</inlineCode>{` on the main body of the application on each page - see `}<a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/axeCheck.js"
        }}>{`axeCheck`}</a></li>
      <li parentName="ul">{`Assert that each navigation is successful`}</li>
    </ul>
    <h2 {...{
      "id": "mocking-api-responses"
    }}>{`Mocking API responses`}</h2>
    <p>{`A mock server runs with the end-to-end tests to allow tests to make production-like calls.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://github.com/department-of-veterans-affairs/va.gov-team/blob/master/platform/testing/end-to-end/vsp-cypress-resources.md#mocks"
      }}>{`Mocks section of Cypress Resources Reference Guide`}</a>{` for detailed mock API examples currently used.`}</p>
    <p>{`Below are some of the commonly used Cypress mocks (accessible from the link above).`}</p>
    <ul>
      <li parentName="ul">{`confirmedVA`}</li>
      <li parentName="ul">{`confirmedCC`}</li>
      <li parentName="ul">{`requests`}</li>
      <li parentName="ul">{`cancelReasons`}</li>
      <li parentName="ul">{`supportedSites`}</li>
      <li parentName="ul">{`facilities`}</li>
      <li parentName="ul">{`facilities983`}</li>
      <li parentName="ul">{`clinicList983`}</li>
      <li parentName="ul">{`slots`}</li>
      <li parentName="ul">{`getVAAppointmentMock`}</li>
      <li parentName="ul">{`getExpressCareRequestCriteriaMock`}</li>
      <li parentName="ul">{`getParentSiteMock`}</li>
    </ul>
    <h2 {...{
      "id": "custom-cypress-commands"
    }}>{`Custom Cypress commands`}</h2>
    <p>{`Cypress supports extending its client api with `}<a parentName="p" {...{
        "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands"
      }}>{`custom commands`}</a>{`.`}</p>
    <p>{`Below are some of the commonly used custom Cypress commands.`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/axeCheck.js"
        }}>{`axeCheck`}</a>{` - Callback from a11y check that logs aXe violations to console output.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/expandAccordions.js"
        }}>{`expandAccordions`}</a>{` - Expands all accordions and AdditionalInfo components.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/hasCount.js"
        }}>{`injectAxeThenAxeCheck`}</a>{` - Combines two common, sequentially called functions.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/login.js"
        }}>{`login`}</a>{` - Simulates a logged in session.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/syncFixtures.js"
        }}>{`syncFixtures`}</a>{` - Runs task to sync fixtures under a temp path in the Cypress fixtures folder then overwrites `}<inlineCode parentName="li">{`cy.fixture`}</inlineCode>{` and the fixture shorthand in `}<inlineCode parentName="li">{`cy.route`}</inlineCode>{` to look for fixtures under that temp path.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/upload.js"
        }}>{`upload`}</a>{` - Workaround to support file upload functionality in tests, which is currently not officially implemented.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/src/platform/testing/e2e/cypress/support/commands/viewportPreset.js"
        }}>{`viewportPreset`}</a>{` - Sets the viewport by preset name.`}</li>
    </ul>
    <h2 {...{
      "id": "helpers"
    }}>{`Helpers`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/department-of-veterans-affairs/va.gov-team/tree/master/platform/testing/end-to-end"
      }}>{`Cypress Resources Reference Guide`}</a>{` contains a list of currently utilized Cypress & VAOS Helpers adn Appointment Helpers.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      